import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Lock, User, LogIn, Sparkles, AlertCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember_me: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Carica credenziali salvate al mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('saved_credentials');
    if (savedCredentials) {
      try {
        const decoded = atob(savedCredentials);
        const credentials = JSON.parse(decoded);
        setFormData({
          username: credentials.username || '',
          password: credentials.password || '',
          remember_me: true
        });
      } catch (error) {
        console.error('Error loading saved credentials:', error);
        localStorage.removeItem('saved_credentials');
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.username.trim()) {
      setError('Inserisci il nome utente');
      return;
    }
    if (!formData.password.trim()) {
      setError('Inserisci la password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/login`, {
        username: formData.username.trim(),
        password: formData.password,
        remember_me: formData.remember_me
      });
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      if (formData.remember_me) {
        const credentials = {
          username: formData.username,
          password: formData.password
        };
        const encoded = btoa(JSON.stringify(credentials));
        localStorage.setItem('saved_credentials', encoded);
      } else {
        localStorage.removeItem('saved_credentials');
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      toast.success(`Benvenuto ${response.data.user.nome_completo || response.data.user.username}!`);
      onLogin(response.data.user);
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Errore di connessione al server';
      
      if (errorMessage.toLowerCase().includes('password')) {
        setError('Password non corretta');
      } else if (errorMessage.toLowerCase().includes('username') || errorMessage.toLowerCase().includes('utente')) {
        setError('Nome utente non trovato');
      } else {
        setError(errorMessage);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgetCredentials = () => {
    localStorage.removeItem('saved_credentials');
    setFormData({
      username: '',
      password: '',
      remember_me: false
    });
    toast.success('Credenziali dimenticate');
  };

  return (
    <div className="h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/images/login-bg.png)' }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Toaster per notifiche */}
      <Toaster position="top-center" richColors />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo e Header */}
        <div className="text-center mb-4">
          <div className="relative inline-block mb-2">
            <img 
              src="/images/logo-ristorantissimo.png" 
              alt="Ristorantissimo Logo" 
              className="h-44 sm:h-52 w-auto object-contain drop-shadow-2xl"
            />
          </div>
          <p className="tagline-animated text-lg sm:text-xl tracking-wider flex items-center justify-center gap-2 flex-wrap">
            <span className="tagline-fill">IL TUO LOCALE,</span>
            <span className="tagline-outline">SEMPRE SOTTO CONTROLLO</span>
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-zinc-900/80 backdrop-blur-xl rounded-2xl border border-cyan-500/20 shadow-2xl shadow-cyan-500/5 overflow-hidden">
          {/* Card Header */}
          <div className="px-6 pt-5 pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Lock className="h-4 w-4 text-black" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Accedi</h2>
                <p className="text-xs text-gray-400">Inserisci le tue credenziali</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Username */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">
                Username
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <User className="h-4 w-4 text-slate-500" />
                </div>
                <input
                  type="text"
                  placeholder="Inserisci username..."
                  value={formData.username}
                  onChange={(e) => {
                    setError('');
                    setFormData({...formData, username: e.target.value});
                  }}
                  className={`w-full h-11 pl-10 pr-4 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm ${
                    error && error.toLowerCase().includes('utente') ? 'border-red-500' : 'border-slate-700'
                  }`}
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Lock className="h-4 w-4 text-slate-500" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Inserisci password..."
                  value={formData.password}
                  onChange={(e) => {
                    setError('');
                    setFormData({...formData, password: e.target.value});
                  }}
                  className={`w-full h-11 pl-10 pr-12 bg-slate-800/50 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm ${
                    error && error.toLowerCase().includes('password') ? 'border-red-500' : 'border-slate-700'
                  }`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember me & Forget */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.remember_me}
                    onChange={(e) => setFormData({...formData, remember_me: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-4 h-4 rounded border-2 border-slate-600 peer-checked:border-blue-500 peer-checked:bg-blue-500 transition-all flex items-center justify-center">
                    {formData.remember_me && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                  Ricordami
                </span>
              </label>
              
              {localStorage.getItem('saved_credentials') && (
                <button
                  type="button"
                  onClick={handleForgetCredentials}
                  className="text-xs text-slate-500 hover:text-blue-400 transition-colors"
                >
                  Dimentica accesso
                </button>
              )}
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-[#0B0C3F] font-semibold rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group text-sm"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Accesso in corso...</span>
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  <span>Accedi</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-4">
          © {new Date().getFullYear()} Ristorantissimo • Tutti i diritti riservati
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
